import { Box, IconButton, Link, Stack, Typography } from "@mui/material";
import {
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import React, { FC, useMemo } from "react";
import { Link as RouteLink } from "react-router-dom";
import { CellProps, Column, Row } from "react-table";

import { getCreditEvaluations } from "api/creditEvaluations";
import { creditEvaluationsKeys } from "api/creditEvaluations/queries";
import { CreditEvaluationsType } from "api/creditEvaluations/types";
import { QueryParamsType } from "api/utils";
import { hasRole } from "components/stores/UserStore";
import Table from "components/Table";
import { dateFormatter } from "components/Table/formatters/dateFormatter";
import useRemoteTableLogic from "components/Table/useRemoteTableLogic";
import UserAvatar from "components/UserAvatar/UserAvatar";
import { generateCreditScoreColor } from "utils/generateCreditScoreColor/generateCreditScoreColor";
import { getFileLink } from "utils/getFile";

import { AffordabilityFormatter } from "./formatters/AffordabilityFormatter";
import { useCreditEvaluationsFilter } from "./useCreditEvaluationsFilter";

interface CreditEvaluationsTableProps {
  /** When provided, search is rendered in PageLayout and table uses these filters */
  filters?: QueryParamsType<CreditEvaluationsType>;
}

const CreditEvaluationsTable: FC<CreditEvaluationsTableProps> = ({
  filters: filtersFromProps,
}) => {
  const filterHook = useCreditEvaluationsFilter();
  const filters = filtersFromProps ?? filterHook.filters;

  const { data, status, sortOptions, paginationOptions } = useRemoteTableLogic(
    creditEvaluationsKeys.list(),
    getCreditEvaluations,
    filters,
  );

  const columns = useMemo<Column<CreditEvaluationsType>[]>(
    () => [
      {
        id: "_expand",
        Header: "",
        width: 48,
        disableSortBy: true,
        Cell: ({ row }: CellProps<CreditEvaluationsType>) =>
          row.canExpand ? (
            <IconButton
              size="small"
              {...row.getToggleRowExpandedProps()}
              sx={{ p: 0.5 }}
            >
              {row.isExpanded ? (
                <ChevronDownIcon size={20} />
              ) : (
                <ChevronRightIcon size={20} />
              )}
            </IconButton>
          ) : null,
      },
      {
        id: "customer",
        Header: "Customer",
        Cell: ({ row }: CellProps<CreditEvaluationsType>) => {
          const customer = row.original.customer;
          const name =
            `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim() ||
            "—";
          const customerId = customer?._id;
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <UserAvatar size={36} fontSize={16} userName={name} />
              {customerId ? (
                <Link component={RouteLink} to={`/customers/${customerId}`}>
                  <Typography>{name}</Typography>
                </Link>
              ) : (
                <Typography>{name}</Typography>
              )}
            </Stack>
          );
        },
      },
      {
        id: "creditScores",
        Header: "Credit Score",
        Cell: ({ row }: CellProps<CreditEvaluationsType>) =>
          row.original.creditScores?.length ? (
            <Stack spacing={0.5}>
              {row.original.creditScores.map((creditScore: any) => (
                <Typography key={creditScore.type}>
                  {creditScore.type}:{" "}
                  <span
                    style={{
                      fontWeight: "bold",
                      marginLeft: 4,
                      color: generateCreditScoreColor(creditScore.score),
                    }}
                  >
                    {creditScore.score}
                  </span>
                </Typography>
              ))}
            </Stack>
          ) : (
            <span>—</span>
          ),
      },
      {
        id: "affordability",
        Header: "Affordability",
        Cell: ({ row }: CellProps<CreditEvaluationsType>) => (
          <AffordabilityFormatter value={row.original.affordability} />
        ),
      },
      {
        accessor: "dealStatus",
        Header: "Deal Status",
      },
      ...(hasRole("admin")
        ? [
            {
              accessor: "leadSource",
              Header: "Lead Source",
            } as Column<CreditEvaluationsType>,
          ]
        : []),
      {
        id: "reports",
        Header: "Reports",
        Cell: ({ row }: CellProps<CreditEvaluationsType>) => (
          <Stack direction="column" spacing={0.5}>
            <Link
              component={RouteLink}
              to={`/credit-evaluations/${row.original._id}`}
            >
              Credit Evaluation
            </Link>
            <Link
              component="a"
              href={getFileLink(row.original.html?.replace("html", "pdf"))}
              target="_blank"
              rel="noopener noreferrer"
            >
              PDF Report
            </Link>
          </Stack>
        ),
      },
      {
        id: "reportDate",
        Header: "Report Date",
        Cell: ({ row }: CellProps<CreditEvaluationsType>) => (
          <span>
            {row.original.reportDate
              ? dateFormatter(row.original.reportDate)
              : "—"}
          </span>
        ),
        align: "right",
      },
    ],
    [],
  );

  // Return a single detail row per evaluation so the table can show one expanded section.
  // Must not return the same row (causes infinite recursion); use a distinct placeholder
  // so getSubRows(detailRow) is never called with nested data.
  const getSubRows = useMemo(
    () => (originalRow: CreditEvaluationsType) => {
      if (originalRow._id && String(originalRow._id).endsWith("-detail")) {
        return [];
      }
      return [
        {
          _id: `${originalRow._id}-detail`,
          notes: originalRow.notes,
        } as CreditEvaluationsType,
      ];
    },
    [],
  );

  // Expand rows that have notes by default
  const defaultExpanded = useMemo(() => {
    const expanded: Record<string, boolean> = {};
    data.forEach((row) => {
      const notes = row.notes;
      if (notes && typeof notes === "string" && notes.trim()) {
        expanded[row._id] = true;
      }
    });
    return expanded;
  }, [data]);

  return (
    <Stack spacing={2} flex={1} height="100%" overflow="hidden">
      <Table<CreditEvaluationsType>
        key={data.length ? "has-data" : "no-data"}
        data={data}
        columns={columns}
        sort={sortOptions}
        pagination={paginationOptions}
        status={status}
        filters={filters}
        getSubRows={getSubRows}
        getRowId={(row, index, parent) =>
          (row as CreditEvaluationsType)._id ??
          (parent ? `${parent.id}.${index}` : String(index))
        }
        initialState={{
          expanded: defaultExpanded,
          sortBy: [
            {
              id: "reportDate",
              desc: true,
            },
          ],
        }}
        expandedState={defaultExpanded}
        autoResetExpanded={false}
        subComponent={(row: Row<CreditEvaluationsType>) => {
          const notes = row.original?.notes;
          const text = notes && typeof notes === "string" ? notes.trim() : "";

          return (
            <Box
              component="td"
              colSpan={columns.length + 1}
              sx={{
                p: 2,
                bgcolor: "grey.100",
                borderBottom: "1px solid",
                borderColor: "divider",
                verticalAlign: "top",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Notes
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: "pre-wrap", m: 0 }}
              >
                {text || "—"}
              </Typography>
            </Box>
          );
        }}
      />
    </Stack>
  );
};

export default CreditEvaluationsTable;
